import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  renderStlThumbnail,
  renderMeshesThumbnail,
} from '@/components/mesh-viewer/renderStlThumbnail';
import type { TessellatedMesh } from '@/workers/stepTessellator.worker';
import {
  enqueueThumbnail,
  __resetThumbnailQueueForTest,
} from '@/components/mesh-viewer/thumbnailRenderQueue';
import {
  getSharedThumbnailDeviceRequestCount,
  __resetSharedThumbnailDeviceForTest,
} from '@/components/mesh-viewer/sharedThumbnailDevice';

export const Route = createFileRoute('/_app/debug/thumbnail/')({
  component: ThumbnailHarness,
});

// Programmatic entries so the Playwright E2E can drive the headless render via
// `page.evaluate`. E2E/debug seam only — the geometry-list thumbnail component
// imports `renderStlThumbnail` / `enqueueThumbnail` directly, not this
// auth-guarded debug route.
declare global {
  interface Window {
    // Render one STL → PNG data URL (ENG-461).
    __renderThumbnail?: (base64Stl: string, size?: number) => Promise<string>;
    // Render an N-tile list through the real queue (serial-but-warm shared
    // device), returning each PNG data URL plus how many GPU devices the batch
    // requested (1 ⇒ shared). Drives the ENG-883 concurrent-render E2E.
    __renderThumbnailBatch?: (
      base64Stls: string[],
      size?: number,
    ) => Promise<{ dataUrls: string[]; deviceRequests: number }>;
    // Render a synthetic cylinder twice — with occt-style per-vertex normals
    // (smooth shading) and without (flat derivative shading) — through the
    // real headless renderer. Drives the smooth-shading E2E: the frames must
    // both draw geometry and must differ.
    __renderPreviewSmoke?: (
      size?: number,
    ) => Promise<{ smooth: string; flat: string }>;
  }
}

const webgpuSupported = typeof navigator !== 'undefined' && 'gpu' in navigator;

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

// Synthetic open cylinder (side wall only) with exact per-vertex radial
// normals — the shape occt-import-js would emit for a curved CAD face. Two
// vertex rings, shared between adjacent quads, so smooth shading has real
// shared-vertex normals to interpolate; the flat render of the same geometry
// shows one shade per facet instead of a continuous gradient.
function buildCylinderMesh(): TessellatedMesh {
  const SEG = 32;
  const R = 1;
  const H = 2;
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= SEG; i++) {
    const t = (i / SEG) * Math.PI * 2;
    const nx = Math.cos(t);
    const ny = Math.sin(t);
    for (const z of [-H / 2, H / 2]) {
      positions.push(R * nx, R * ny, z);
      normals.push(nx, ny, 0);
    }
  }
  for (let i = 0; i < SEG; i++) {
    const a = 2 * i; // bottom ring, this segment
    const b = a + 1; // top ring, this segment
    const c = a + 2; // bottom ring, next segment
    const d = a + 3; // top ring, next segment
    indices.push(a, c, b, b, c, d);
  }
  return {
    name: 'preview-smoke-cylinder',
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    normals: new Float32Array(normals),
    brepFaceFirsts: new Uint32Array([0]),
    brepFaceLasts: new Uint32Array([indices.length / 3 - 1]),
    brepFaceColors: new Float32Array([1, 1, 1]),
  };
}

/**
 * Debug harness for the headless STL → PNG thumbnail path (ENG-461). Pick an
 * STL to render it to a PNG in-browser for manual inspection, or call
 * `window.__renderThumbnail` programmatically (the Playwright E2E does). The
 * reusable core is `renderStlThumbnail` — the geometry-list thumbnail component
 * drives that directly, not this debug route.
 */
function ThumbnailHarness() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pngUrl, setPngUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [size, setSize] = useState(512);

  // Install the programmatic entries for the E2E.
  useEffect(() => {
    window.__renderThumbnail = async (base64Stl, s = 512) => {
      const blob = await renderStlThumbnail(base64ToBytes(base64Stl), {
        size: s,
      });
      return blobToDataUrl(blob);
    };

    // Render an N-tile list through the real queue (serial-but-warm shared
    // device). Distinct keys so each tile actually renders; the count of
    // devices requested across the batch proves they share one (ENG-883).
    window.__renderThumbnailBatch = async (base64Stls, s = 256) => {
      __resetThumbnailQueueForTest();
      __resetSharedThumbnailDeviceForTest();
      const blobs = await Promise.all(
        base64Stls.map((b64, i) =>
          enqueueThumbnail(async () => base64ToBytes(b64), `batch:${i}`, s),
        ),
      );
      const dataUrls = await Promise.all(blobs.map(blobToDataUrl));
      return {
        dataUrls,
        deviceRequests: getSharedThumbnailDeviceRequestCount(),
      };
    };

    // Same cylinder with and without normals — only the shading may differ.
    window.__renderPreviewSmoke = async (s = 256) => {
      const cylinder = buildCylinderMesh();
      const smooth = await renderMeshesThumbnail([cylinder], { size: s });
      const flat = await renderMeshesThumbnail(
        [{ ...cylinder, normals: null }],
        {
          size: s,
        },
      );
      return {
        smooth: await blobToDataUrl(smooth),
        flat: await blobToDataUrl(flat),
      };
    };

    return () => {
      delete window.__renderThumbnail;
      delete window.__renderThumbnailBatch;
      delete window.__renderPreviewSmoke;
    };
  }, []);

  // Object-URL hygiene: revoke the previous render's URL when it's replaced.
  useEffect(() => {
    if (!pngUrl) return;
    return () => URL.revokeObjectURL(pngUrl);
  }, [pngUrl]);

  const handleRender = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const blob = await renderStlThumbnail(bytes, { size });
      setPngUrl(URL.createObjectURL(blob));
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!webgpuSupported) {
    return (
      <div className="p-4 text-muted-foreground">
        WebGPU is not supported in this browser. Try the latest Chrome or Edge.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <h1 className="text-lg font-semibold">Thumbnail render (ENG-461)</h1>
      <p className="max-w-prose text-sm text-muted-foreground">
        Renders an STL to a PNG through the headless renderer
        (`renderStlThumbnail`) — the same path the thumbnail service uses. Pick
        a `.stl` file and render.
      </p>
      <div className="flex items-center gap-2">
        <input ref={fileInputRef} type="file" accept=".stl" />
        <label className="text-sm text-muted-foreground">
          size{' '}
          <input
            type="number"
            value={size}
            min={32}
            max={2048}
            step={32}
            onChange={(e) => setSize(Number(e.target.value) || 512)}
            className="w-20 rounded border border-border bg-background px-1"
          />
        </label>
        <button
          onClick={handleRender}
          disabled={busy}
          className="rounded bg-primary px-3 py-1 text-primary-foreground disabled:opacity-50"
        >
          {busy ? 'Rendering…' : 'Render'}
        </button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {pngUrl && (
        <img
          src={pngUrl}
          alt="rendered thumbnail"
          className="border border-border"
          style={{ width: size, height: size }}
        />
      )}
    </div>
  );
}
